/**
 * Portfolio Intelligence product page — BP-023.
 * Cash-flow health, cap rate, and expiring funding across held assets.
 */
import { Building2, DollarSign, Percent, Clock, AlertTriangle, Landmark } from "lucide-react";
import ProductPageTemplate, { ProductScreen } from "../ProductPageTemplate";

const heroMockup = (
  <ProductScreen title="portfolio / 18 assets · $4.2M equity">
    <div className="grid grid-cols-4 gap-2 text-xs">
      {[
        ["NOI / mo", "$28.4k", "+6%"],
        ["Cap Rate", "8.4%", "avg"],
        ["Occupancy", "94%", "17/18"],
        ["Cash-out", "$412k", "unlocked"],
      ].map(([k, v, s]) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2">
          <div className="uppercase text-[9px] text-slate-500">{k}</div>
          <div className="text-base font-bold text-slate-900">{v}</div>
          <div className="text-[9px] text-emerald-600 font-semibold mt-0.5">{s}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 pt-3 border-t border-slate-200 space-y-1 text-[11px]">
      <div className="flex items-center gap-2 text-red-600">
        <AlertTriangle className="w-3 h-3" /> 2 loans maturing &lt; 90 days
      </div>
      <div className="flex items-center gap-2 text-amber-600">
        <Clock className="w-3 h-3" /> 1 lease expiring in 45 days
      </div>
      <div className="flex items-center gap-2 text-emerald-600">
        <Landmark className="w-3 h-3" /> 3 assets qualify for BRRRR refi
      </div>
    </div>
  </ProductScreen>
);

export default function PortfolioIntelligencePage() {
  return (
    <ProductPageTemplate
      slug="portfolio-intelligence"
      bp="BP-023 · Portfolio Intelligence"
      eyebrow="Product · Portfolio Intelligence"
      title="Hold like a fund."
      titleAccent="Manage like a pro."
      subtitle="Roll every held asset into a single command view — cash-flow health, cap rate, occupancy, loan-maturity risk, and BRRRR-refi opportunities — all cited to the underlying deterministic engines."
      bullets={[
        "Cash-flow scoring across the entire portfolio, per-asset drill-through",
        "Expiring funding + lease radar with 90/60/30-day windows",
        "BRRRR refinance opportunities surfaced automatically as equity builds",
      ]}
      heroMockup={heroMockup}
      capabilities={[
        { icon: Building2, title: "Asset Register", desc: "Every held property, its rent, expenses, financing, and current market comp — in one grid." },
        { icon: DollarSign, title: "Cash-Flow Health", desc: "Per-asset and portfolio-level NOI, DSCR, and free-cash-flow health scores." },
        { icon: Percent, title: "Cap Rate Live", desc: "Cap rate re-computed against current market comps automatically — stale numbers eliminated." },
        { icon: Clock, title: "Maturity Radar", desc: "Balloon dates, ARM resets, lease expiries — proactive alerts before they surprise you." },
        { icon: AlertTriangle, title: "Risk Bands", desc: "Assets slipping into 'Watch' fire a proactive digest. No more discovering leaks at tax time." },
        { icon: Landmark, title: "BRRRR Radar", desc: "As equity builds, refi opportunities appear automatically. Recycle capital, not effort." },
      ]}
      howItWorks={[
        { step: 1, title: "Load", desc: "Import current holdings — CSV, QuickBooks, or one-by-one. Each becomes a portfolio asset." },
        { step: 2, title: "Enrich", desc: "Every asset re-scored nightly against local market comps and rent data." },
        { step: 3, title: "Rank", desc: "Portfolio-level health score, plus per-asset bands. Roll up into Executive Dashboard." },
        { step: 4, title: "Alert", desc: "Maturities, lease expiries, and refi windows fire proactively — never miss a window." },
      ]}
      integrations={[
        { bp: "BP-023", name: "Portfolio Intelligence" },
        { bp: "BP-021", name: "Cash Flow" },
        { bp: "BP-004", name: "ARV Comps" },
        { bp: "BP-024", name: "Market Intelligence" },
        { bp: "BP-014", name: "Strategy Comparison" },
        { bp: "BP-019", name: "Exec Dashboard" },
        { bp: "BP-025", name: "Coach Briefings" },
        { bp: "BP-002", name: "Property Workspace" },
      ]}
      proof={{
        stat: "$412k",
        label: "avg. equity unlocked via BRRRR radar",
        quote: "We had three refi-ready properties sitting there we hadn't noticed. Portfolio Intelligence caught them the same week we plugged in.",
        attribution: "Buy-and-hold operator · 22 doors",
      }}
      related={[
        { title: "Executive Dashboard", to: "/products/executive-dashboard", desc: "Roll portfolio metrics into six-dimension business health." },
        { title: "Market Intelligence", to: "/products/market-intelligence", desc: "See how each held asset tracks vs. its local market." },
        { title: "Strategy Comparison", to: "/products/strategy-comparison", desc: "Score alternate exits for every held asset in one click." },
      ]}
    />
  );
}
